import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ReturnsReception, ReturnsValidations } from './ReturnsWork';
import { Plus, Search } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useToast } from '../../components/ui/Toast';
import { formatDate, formatEUR } from '../../lib/format';
import { cn } from '../../utils/cn';
import {
  cmdHeaders,
  returnError,
  Field,
  reasonLabel,
  StatusBadge,
  tdCls,
  thCls,
  useReturnsRole,
  type ReturnRow,
} from '../../components/returns/returnsShared';

/**
 * Returns — every return, from the counter or the garage portal (returns v2, the owner's
 * "MecaRadar Retours" prototype): what needs someone today, the indicators, then the list by tab
 * with the next action on each line.
 */

interface Alert {
  level: 'info' | 'warn' | 'bad';
  code: string;
  returnId?: string;
  returnNumber?: string;
  supplierReturnId?: string;
  params: Record<string, unknown>;
}
interface Kpis {
  toDecide: number;
  awaitingPart: number;
  toControl: number;
  toRefund: number;
  atSupplier: number;
  cashToApprove: number;
  creditMonth: number;
  rate: number;
  supplierExpected: number;
}
/** One page, the queues as tabs (owner, 2026-10-16): reception and validations are views of the same returns. */
const TABS = ['TODO', 'RECEPTION', 'VALIDATIONS', 'CLOSED', 'DEAD', 'ALL'] as const;
type Tab = (typeof TABS)[number];
const QUEUE_TABS: Tab[] = ['RECEPTION', 'VALIDATIONS'];

export function AlertBanners({ alerts }: { alerts: Alert[] }) {
  const { t } = useTranslation();
  if (!alerts.length) return null;
  return (
    <div className="mb-3 grid gap-2">
      {alerts.map((a, i) => (
        <div
          key={i}
          className={cn(
            'flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
            a.level === 'bad' && 'bg-destructive/[.12] text-destructive',
            a.level === 'warn' && 'bg-warning/[.13] text-warning',
            a.level === 'info' && 'bg-primary-soft text-primary'
          )}
        >
          <span className="flex-1">
            {t(`returnsV2.alert.${a.code}`, {
              number: a.returnNumber ?? a.params.number,
              ...a.params,
              due: formatDate(a.params.due as string),
              shipped: formatDate(a.params.shipped as string),
              amount: formatEUR(a.params.amount as number),
            })}
          </span>
          {a.returnId ? (
            <Link className="underline underline-offset-2" to={`/returns/${a.returnId}`}>
              {t('returnsV2.open')}
            </Link>
          ) : a.supplierReturnId ? (
            <Link className="underline underline-offset-2" to="/returns/suppliers">
              {t('returnsV2.see')}
            </Link>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function ReturnsTable({
  rows,
  action,
}: {
  rows: ReturnRow[];
  action?: (r: ReturnRow) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!rows.length)
    return (
      <div className="px-4 py-12 text-center text-sm text-muted-foreground">
        {t('returnsV2.list.empty')}
      </div>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className={thCls}>{t('returnsV2.list.return')}</th>
            <th className={thCls}>{t('returnsV2.list.customer')}</th>
            <th className={thCls}>{t('returnsV2.list.origin')}</th>
            <th className={thCls}>{t('returnsV2.list.parts')}</th>
            <th className={cn(thCls, 'text-right')}>{t('returnsV2.list.amount')}</th>
            <th className={thCls}>{t('returnsV2.list.status')}</th>
            <th className={thCls} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="cursor-pointer border-b last:border-b-0 hover:bg-muted/50"
              onClick={() => navigate(`/returns/${r.id}`)}
            >
              <td className={tdCls}>
                <span className="font-mono text-xs font-medium">{r.returnNumber}</span>
                <div className="text-xs text-muted-foreground">
                  {formatDate(r.requestedOn ?? r.createdAt)}
                </div>
              </td>
              <td className={tdCls}>
                <div className="font-medium">{r.customerName ?? '—'}</div>
                <div className="text-xs text-muted-foreground">
                  {t(`returnsV2.customerType.${r.customerType ?? 'B2C'}`)}
                  {r.channel === 'WEB' ? ` · ${t('returnsV2.list.online')}` : ''}
                </div>
              </td>
              <td className={tdCls}>
                {t(`returnsV2.origin.${r.origin}`)}
                <div className="text-xs text-muted-foreground">
                  {r.createdBy ? t('returnsV2.list.by', { name: r.createdBy }) : ''}
                  {r.pickup === 'DRIVER' ? ` · ${t('returnsV2.list.driver')}` : ''}
                  {r.noReceipt ? ` · ${t('returnsV2.list.noReceipt')}` : ''}
                  {r.legacy ? ` · ${t('returnsV2.list.legacy')}` : ''}
                </div>
              </td>
              <td className={tdCls}>
                {r.lines.map((l, i) => (
                  <div key={i} className="font-mono text-xs">
                    {l.sku} × {l.qty}
                  </div>
                ))}
                <div className="text-xs text-muted-foreground">
                  {[...new Set(r.lines.map((l) => reasonLabel(t, l.reason)))].join(' · ')}
                </div>
              </td>
              <td className={cn(tdCls, 'text-right tabular-nums')}>
                {formatEUR(r.totalTtc)}
                {r.feeTtc > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {t('returnsV2.list.fee', { amount: formatEUR(r.feeTtc) })}
                  </div>
                )}
              </td>
              <td className={tdCls}>
                <StatusBadge status={r.status} refundInProgress={r.refundInProgress} />
              </td>
              <td
                className={cn(tdCls, 'whitespace-nowrap text-right')}
                onClick={(e) => e.stopPropagation()}
              >
                {action?.(r)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The next step a line offers, by status and role (the prototype's nextAction). */
export function useNextAction(reload: () => void) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { push } = useToast();
  const { can } = useReturnsRole();
  return function nextAction(r: ReturnRow) {
    if (r.legacy) return null;
    const open = () => navigate(`/returns/${r.id}`);
    if (r.status === 'REQUESTED' && can('decide'))
      return (
        <Button size="sm" onClick={open}>
          {t('returnsV2.action.decide')}
        </Button>
      );
    if (r.status === 'APPROVED' && can('receive')) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            try {
              await apiClient.post('/returns/receive', { number: r.returnNumber }, cmdHeaders());
              push({
                tone: 'success',
                message: t('returnsV2.toast.received', { number: r.returnNumber }),
              });
              navigate(`/returns/${r.id}`);
            } catch (err) {
              push({ tone: 'error', message: returnError(t, err) });
              reload();
            }
          }}
        >
          {t('returnsV2.action.receive')}
        </Button>
      );
    }
    if (r.status === 'RECEIVED' && can('receive'))
      return (
        <Button size="sm" onClick={open}>
          {t('returnsV2.action.control')}
        </Button>
      );
    if (r.status === 'INSPECTED' && !r.refundInProgress && can('counter'))
      return (
        <Button size="sm" onClick={open}>
          {t('returnsV2.action.refund')}
        </Button>
      );
    return null;
  };
}

export default function ReturnsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError } = useToast();
  const { can, role } = useReturnsRole();
  const [params, setParams] = useSearchParams();
  // The tab lives in the address (?tab=RECEPTION), so a menu link, a bookmark or the back button
  // lands on it; the warehouse opens on what it does: reception.
  const urlTab = params.get('tab') as Tab | null;
  const tab: Tab =
    urlTab && (TABS as readonly string[]).includes(urlTab)
      ? urlTab
      : role === 'WAREHOUSE_KEEPER'
        ? 'RECEPTION'
        : 'TODO';
  const setTab = (key: Tab) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('tab', key);
        return n;
      },
      { replace: true }
    );
  const [q, setQ] = useState('');
  const [origin, setOrigin] = useState('');
  const [rows, setRows] = useState<ReturnRow[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [k, setK] = useState<Kpis | null>(null);

  const load = useCallback(() => {
    if (QUEUE_TABS.includes(tab)) return;
    const qs = new URLSearchParams();
    if (tab !== 'ALL') qs.set('group', tab);
    if (q.trim()) qs.set('q', q.trim());
    if (origin) qs.set('origin', origin);
    apiClient
      .get<ReturnRow[]>(`/returns/board?${qs}`)
      .then((r) => setRows(r.data))
      .catch((e) => {
        setRows([]);
        showApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, origin]);

  const loadSide = useCallback(() => {
    apiClient
      .get<Alert[]>('/returns/alerts')
      .then((r) => setAlerts(r.data))
      .catch(() => setAlerts([]));
    apiClient
      .get<Kpis & { byStatus: Record<string, number> }>('/returns/kpis')
      .then((r) => {
        setK(r.data);
        const b = r.data.byStatus ?? {};
        const sum = (ss: string[]) => ss.reduce((s, x) => s + (b[x] ?? 0), 0);
        setCounts({
          TODO: sum(['REQUESTED', 'APPROVED', 'RECEIVED', 'INSPECTED', 'AWAITING_SUPPLIER']),
          RECEPTION: sum(['APPROVED', 'RECEIVED']),
          VALIDATIONS: sum(['REQUESTED']) + r.data.cashToApprove,
          CLOSED: sum(['CLOSED', 'REFUNDED']),
          DEAD: sum(['REJECTED', 'CANCELLED', 'EXPIRED']),
          ALL: Object.values(b).reduce((s, n) => s + n, 0),
        });
      })
      .catch(() => setK(null));
  }, []);

  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
  }, [load]);
  useEffect(loadSide, [loadSide]);
  const next = useNextAction(() => {
    load();
    loadSide();
  });

  const tile = (label: string, value: React.ReactNode, sub: string, alert = false, to?: Tab) => (
    <div
      className={cn(
        'rounded-lg border bg-card px-4 py-3 shadow-sm',
        to && 'cursor-pointer hover:border-muted-foreground'
      )}
      onClick={to ? () => setTab(to) : undefined}
      role={to ? 'button' : undefined}
      tabIndex={to ? 0 : undefined}
      onKeyDown={
        to
          ? (e) => {
              if (e.key === 'Enter') setTab(to);
            }
          : undefined
      }
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn('text-2xl font-bold tabular-nums', alert && 'text-destructive')}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={t('returnsV2.list.title')}
        description={t('returnsV2.list.description')}
        actions={
          can('counter') && (
            <Button size="sm" onClick={() => navigate('/returns/new')}>
              <Plus className="h-4 w-4" /> {t('returnsV2.list.newCounter')}
            </Button>
          )
        }
      />
      <AlertBanners alerts={alerts} />
      {k && (
        <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {tile(
            t('returnsV2.kpi.toDecide'),
            k.toDecide,
            t('returnsV2.kpi.toDecideSub'),
            k.toDecide > 0,
            'VALIDATIONS'
          )}
          {tile(
            t('returnsV2.kpi.awaiting'),
            k.awaitingPart,
            t('returnsV2.kpi.awaitingSub'),
            false,
            'RECEPTION'
          )}
          {tile(
            t('returnsV2.kpi.toControl'),
            k.toControl,
            t('returnsV2.kpi.toControlSub'),
            false,
            'RECEPTION'
          )}
          {tile(t('returnsV2.kpi.toRefund'), k.toRefund, t('returnsV2.kpi.toRefundSub'))}
          {tile(
            t('returnsV2.kpi.atSupplier'),
            k.atSupplier,
            t('returnsV2.kpi.atSupplierSub', { amount: formatEUR(k.supplierExpected) })
          )}
          {tile(
            t('returnsV2.kpi.month'),
            formatEUR(k.creditMonth),
            t('returnsV2.kpi.monthSub', { rate: String(k.rate).replace('.', ',') })
          )}
        </div>
      )}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex gap-1 overflow-x-auto border-b px-3 pt-2" role="tablist">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={cn(
                '-mb-px whitespace-nowrap border-b-[3px] px-3 py-2 text-sm',
                tab === key
                  ? 'border-primary font-semibold text-foreground'
                  : 'border-transparent text-muted-foreground'
              )}
            >
              {t(`returnsV2.list.tab.${key}`)}
              <span
                className={cn(
                  'ml-1.5 rounded-full px-1.5 text-xs',
                  key === 'VALIDATIONS' && (counts[key] ?? 0) > 0
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {counts[key] ?? 0}
              </span>
            </button>
          ))}
        </div>
        {tab === 'RECEPTION' && (
          <ReturnsReception
            embedded
            onChange={() => {
              load();
              loadSide();
            }}
          />
        )}
        {tab === 'VALIDATIONS' && (
          <ReturnsValidations
            embedded
            onChange={() => {
              load();
              loadSide();
            }}
          />
        )}
        {!QUEUE_TABS.includes(tab) && (
          <>
            <div className="flex flex-wrap items-end gap-3 border-b px-4 py-3">
              <Field label={t('returnsV2.list.search')} className="min-w-[16rem] flex-1">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('returnsV2.list.searchHint')}
                  />
                </div>
              </Field>
              <Field label={t('returnsV2.list.origin')}>
                <Select value={origin} onChange={(e) => setOrigin(e.target.value)}>
                  <option value="">{t('returnsV2.list.allOrigins')}</option>
                  <option value="COUNTER">{t('returnsV2.origin.COUNTER')}</option>
                  <option value="PORTAL">{t('returnsV2.origin.PORTAL')}</option>
                </Select>
              </Field>
            </div>
            {rows === null ? (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                {t('returnsV2.loading')}
              </div>
            ) : (
              <ReturnsTable rows={rows} action={next} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
